import React from 'react'
import { levelNames, localesInterface } from './main/types'
import { Link } from '@ta-interaktiv/contentbricks'

export const locales: localesInterface = {
  hazards: {
    water: 'Hochwasser',
    avalanche: 'Lawinen',
    landslide: 'Erdrutsch',
    rockfall: 'Felssturz'
  },
  levels: { slight: 'Gering', mean: 'Mittel', substantial: 'Erheblich' },
  perimeter: {
    title: 'Stand der Erfassung',
    hint:
      'Die noch nicht oder teilweise erfassten Gebiete werden in der Karte angezeigt, wenn eine einzelne Gefahr ausgewählt ist.',

    steps: {
      not: 'nicht erfasst',
      partial: 'teilweise erfasst / in Bearbeitung',
      full: 'vollständig erfasst / nicht nötig'
    }
  },
  nominatim: {
    noEntry: 'Keine Ergebnisse gefunden',
    position: 'Mein Standort',
    placeholder: 'Adresse eingeben',
    search: 'Suche...'
  },
  other: {
    titleLevels: 'Gefahrenstufen',
    titlePerimeter: 'Stand der Erfassung',
    teaserTitle: 'Artikel zum Thema',
    credits: ['Recherche', 'Datenbearbeitung', 'Umsetzung']
  },
  map: {
    desktop: {
      activate: 'Karte aktivieren',
      deactivate: 'Scrollen aktivieren'
    },
    mobile: {
      activate: 'Karte aktivieren',
      deactivate: 'Zurück zum Artikel'
    }
  },
  sources: { title: 'Quellen', key: ['Kanton', 'Quelle', 'Stand'] },
  subsections: { search: 'Adresse suchen', key: 'Legende' },
  hints: {
    interaction: 'Hier klicken um mit der Karte zu interagieren',
    hazards: 'Hier klicken um nur eine Gefahr anzuzeigen'
  },
  questions: [
    'Wie informativ war der Beitrag?',
    'Wie gefällt Ihnen die Gestaltung?',
    'Wie gut finden Sie die Bedienung der interaktiven Karte?'
  ],
  attribution: '© Gefahrenkarten der jeweiligen Kantone'
}
export const text = {
  head: {
    title: <>Steht Ihr Haus in einem Gefahrengebiet?</>,
    lead: (
      <>
        Eine neue Version unserer Gefahrenkarte zeigt jetzt, wo Erdrutsch,
        Steinschlag, Fels- und Bergsturz drohen. Zusammen mit den Lawinen- und
        Hochwasserbereichen stehen nun über 300’000 Gebäude in Gefahrengebieten,
        11’000 davon in der höchsten Gefährdungsstufe. Ist auch Ihr Haus
        betroffen? Finden Sie es heraus mit unserer interaktiven Gefahrenkarte.
      </>
    )
  },
  main: (
    <>
      <p>
        Vor einem Jahr hat das Daten- und Rechercheteam von Tamedia eine Karte
        mit den Lawinen- und Hochwassergebieten veröffentlicht. Die neue Version
        zeigt nun alle vier Naturgefahren inklusive Erdrutsch und Felssturz, zu
        dem hier auch der Steinschlag und Bergsturz zählen.
      </p>
      <p>
        Allein die neu hinzugefügten Erdrutschgebiete umfassen mit 959 km2 eine
        Fläche, die so gross ist wie der Kanton Thurgau. Nach dem Hochwasser
        sind am meisten Menschen und Gebäude in der Schweiz durch solche
        Rutschungen bedroht.
      </p>
      <p>
        Die neue Karte gibt nun ein umfassenderes Bild der Naturgefahrenlage.
        Sie basiert auf den offiziellen Gefahrenkarten, welche die Kantone für
        praktisch alle Gemeinden erstellen. Dort sind die betroffenen Gebiete
        für die vier Gefahrentypen genau ausgewiesen.
      </p>
      <p>
        Für dieses Online-Tool wurden die Kantonskarten systematisch
        zusammengetragen (ausser Ob- und Nidwalden, die nicht teilnehmen
        wollten). Sie können damit nun gezielt nach Adressen suchen oder Ihr
        Haus und Ihre Nachbarschaft neu auf alle vier Gefahrenbereiche prüfen.
      </p>
      <p>
        Es zeigt sich, dass total 300’000 Gebäude in der Schweiz in einem
        Gefahrengebiet liegen. Schätzungsweise 1,2 Millionen Menschen leben
        dort. Über 70’000 von ihnen in Bereichen mit der höchsten
        Gefährdungsstufe. Und mit dem Klimawandel wird die Zahl der
        Extremereignisse in der Schweiz laut Experten noch zunehmen.
      </p>
    </>
  ),
  disclaimer: (
    <>
      Gefahrenkarten werden meistens nur für Siedlungsgebiete erstellt.
      Ausserhalb der Siedlungsgebiete können auch Gefahren auftreten, welche aus
      der nachfolgenden Karte nicht ersichtlich werden. Auf die Darstellung von
      Gebieten mit einer sehr tiefen Gefahr (sogenannte «Restgefahr») wurde
      verzichtet. In jedem Fall sind die Original-Gefahrenkarten der Kantone
      ausschlaggebend. Diese sind für alle Kantone auch online einsehbar (siehe
      Links in den Quellenangaben am Ende des Artikels).
    </>
  ),
  levelmeaning: {
    title: <>Das bedeuten die Gefahrengebiete</>,
    sub: (
      <>
        In der Karte sind drei verschiedene Gefahrenstufen eingezeichnet:
        erheblich, mittel und gering. Auf die Darstellung von Gebieten mit sehr
        tiefer Gefährdung (sogenannter Restgefährdung) wurde verzichtet.
        Ebenfalls nicht dargestellt werden Gefahrenhinweisgebiete. In diesen
        können bestimmte Gefahren möglicherweise auftreten, sie liegen jedoch
        normalerweise ausserhalb der besiedelten Gebiete.
      </>
    ),
    levels: {
      substantial: (
        <>
          <b>Erheblich bedeutet,</b> dass Personen sowohl innerhalb als auch
          ausserhalb von Gebäuden gefährdet sind. Mit der plötzlichen Zerstörung
          von Gebäuden ist zu rechnen. In solchen Gebieten werden keine neuen
          Bauten mehr erlaubt. Sind ausgeschiedene Bauzonen noch unbebaut,
          werden sie umgezont. Bestehende Bauten werden in der Regel stehen
          gelassen. Das Risiko darf aber durch Erweiterungen oder Umnutzungen
          nicht erhöht werden. Nutzungsbeschränkungen sind möglich, insbesondere
          bei sensiblen Objekten wie Schulen, Spitälern oder Campingplätzen.
          (Farbe in den offiziellen Gefahrenkarten der Kantone: Rot)
        </>
      ),
      mean: (
        <>
          <b>Mittel bedeutet,</b> dass Personen innerhalb von Gebäuden kaum
          gefährdet sind. Ausserhalb jedoch schon. Mit Schäden an Gebäuden ist
          zu rechnen. Solche Gebiete werden möglichst nicht neu bebaut. Und
          wenn, dann nur mit Auflagen. Sensible Objekte wie Schulen, Spitäler
          oder Campingplätze dürfen nicht neu erstellt werden. Bei bestehenden
          Bauten dürfen Nutzungsbeschränkungen erlassen werden. (Farbe in den
          offiziellen Gefahrenkarten der Kantone: Blau)
        </>
      ),
      slight: (
        <>
          <b>Gering bedeutet,</b> dass Personen kaum gefährdet sind. Erhebliche
          Sachschäden können jedoch auftreten. Gesetzliche Auflagen gibt es nur
          für sensible Objekte wie Schulen, Spitäler oder Campingplätze. Sie
          sollten in solchen Gebieten nur mit grosser Zurückhaltung gebaut
          werden. (Farbe in den offiziellen Gefahrenkarten der Kantone: Gelb).
        </>
      )
    }
  },
  zonedefinition: {
    title: <>So wird bestimmt, wo die Gefahrengebiete liegen</>,
    sub: (
      <>
        Ob ein Gebiet als gefährdet eingestuft wird, hängt davon ab, wie häufig
        und mit welcher Intensität Ereignisse wie Überschwemmungen oder Lawinen
        auftreten. Die Einteilung erfolgt anhand eines
        Intensitäts-Wahrscheinlichkeits-Diagramms:
      </>
    ),
    image: (
      <img
        className='visual'
        src={require('images/matrix_de.svg')}
        width='250px'
      />
    ),
    points: [
      [
        <b>Intensität:</b>,
        <>
          Langsame Prozesse wie der steigende Pegel eines Sees nach lang
          anhaltenden Niederschlägen sind für Menschen tendenziell weniger
          gefährlich als sogenannte «brutale» Ereignisse, die ohne grosse
          Vorwarnzeit auftreten können. Beispiele für brutale Prozesse sind ein
          Felssturz oder ein Gebirgsbach, der nach einem lokalen Gewitter
          plötzlich über die Ufer tritt. Brutale Prozesse werden daher
          tendenziell als gefährlicher beurteilt als langsame.
        </>
      ],
      [
        <b>Wahrscheinlichkeit:</b>,
        <>
          Beim Erstellen von Gefahrenkarten wird unterschieden zwischen
          Ereignissen, die voraussichtlich alle 30, 100 oder 300 Jahre
          eintreten. Eine Zone kann auch dann als erheblich gefährdet eingestuft
          werden, wenn ein bestimmtes Ereignis nur alle 300 Jahre auftritt.
        </>
      ]
    ]
  },
  contentafter: (
    <>
      <h4 className='title'>
        Das ist bei Gefahrenkarten ausserdem zu beachten
      </h4>
      <p>
        Gefahrenkarten können sich über die Zeit verändern. Wird in einem
        Bereich zum Beispiel ein Hochwasserschutzprojekt realisiert, kann das
        dazu führen, dass ein Gefahrengebiet herabgestuft oder ganz gestrichen
        wird. Dank neuer Messtechniken ist es aber auch möglich, dass neue
        Gebiete plötzlich als gefährdet erkannt werden. Noch bis vor wenigen
        Jahren wurden Gutachten zu Naturgefahren vor allem mittels
        Expertenbegehungen vor Ort gemacht. Inzwischen werden auch
        Computermodelle beigezogen.
      </p>
      <p>
        Keinen Einfluss auf die Einteilung nach Gefährdungsstufen hat die
        Nutzung eines Grundstücks. Auch spezifische Schutzmassnahmen nur für ein
        bestimmtes Gebäude beeinflussen die Einteilung nicht.
      </p>
      <p>
        Die schweizweite Gefahrenkarte von Tamedia basiert auf den gegenwärtig
        verfügbaren Gefahrenkarten der Kantone.
      </p>
      <p>
        Schutzbauten, für die in der Regel die Gemeinden zuständig sind,
        fliessen teilweise mit einigen Jahren Verzögerung in die Karte ein. Es
        ist also möglich, dass in der hier vorliegenden Karte einzelne
        Gefahrenzuordnungen nicht mehr aktuell sind, weil die Kantone Änderungen
        noch nicht veröffentlicht haben.
      </p>
    </>
  ),
  reply: (
    <>
      <b>Hinweise an die Redaktion</b>
      <br />
      Leben Sie in einem Gefahrengebiet? Wissen Sie von stark gefährdeten
      sensiblen Objekten? Ihren Hinweis nimmt das Daten-Team gern entgegen unter{' '}
      <Link href='mailto:daten@tamedia.ch' hideIcon>
        daten@tamedia.ch
      </Link>
    </>
  ),
  technical_head: <>Technische Hinweise</>,
  technical: (
    <>
      <p>
        Die Gefahrenkarten der einzelnen Kantone wurden vom Tamedia-Datenteam
        von den jeweiligen Kantonen in elektronischer Form beschafft und in eine
        einheitliche Form gebracht. Die dargestellten Naturgefahren stellen den
        Stand zum Zeitpunkt des Bezugs von den Kantonen dar (unten in der
        Tabelle aufgeführt). Die Karte wird nicht aktualisiert.
      </p>
      <p>
        Ausschlaggebend sind immer die Originaldaten des jeweiligen Kantons.
        Diese können auf den Geoportalen der Kantone betrachtet werden, die
        Links dazu finden sich ebenfalls in der untenstehenden Tabelle.
        Teilweise sind in den kantonalen Geoportalen noch mehr Angaben zu
        finden, zum Beispiel Gefahrenhinweiskarten für Bereiche ausserhalb der
        Siedlungsgebiete. In einzelnen Kantonen gibt es Gebiete, die noch nicht
        in den offiziellen Gefahrenkarten erfasst wurden. Diese werden in der
        interaktiven Karte schraffiert dargestellt. Das{' '}
        <Link href='https://www.bafu.admin.ch/bafu/de/home/themen/naturgefahren/fachinformationen/naturgefahrensituation-und-raumnutzung/gefahrengrundlagen/gefahrenkarten--intensitaetskarten-und-gefahrenhinweiskarten.html'>
          Bundesamts für Umwelt
        </Link>{' '}
        liefert zudem eine Übersicht, in welchen Gemeinden die Gefahrenkarten
        noch nicht vollständig vorliegen.
      </p>
    </>
  )
}
